import { BreakawaySection, CombinedPerformance } from '../../types/breakawayHull';
import { getDriveTons } from '../../constants/drives';
import { calculatePowerRequirements } from './powerCalculations';
import { calculateJumpFuelConsumption, calculatePowerPlantFuelConsumption } from './fuelCalculations';

/**
 * Calculate combined performance for breakaway hull sections
 * Following Traveller rules: "drives, power plants and weapons can all be combined when calculating performance"
 */
export const calculateCombinedPerformance = (sections: BreakawaySection[]): CombinedPerformance => {
  let totalManeuverTons = 0;
  let totalJumpTons = 0;
  let totalReactionTons = 0;
  let totalPower = 0;
  let totalCost = 0;
  let totalArmorWeight = 0;
  let totalArmorCost = 0;
  const totalTonnage = sections.reduce((sum, section) => sum + section.size, 0);

  // Sum up all drives and power plants from all sections
  sections.forEach(section => {
    // For combined performance, we calculate drive tons based on section size
    // Then we'll calculate the combined rating based on total tonnage
    
    // Maneuver drives
    if (section.drives.maneuver) {
      // Extract actual tonnage from description if available
      const description = section.drives.maneuver.description || '';
      const tonsMatch = description.match(/\(([\d.]+) tons\)/);
      if (tonsMatch) {
        totalManeuverTons += parseFloat(tonsMatch[1]);
      } else {
        // Fallback to calculated tonnage
        totalManeuverTons += getDriveTons(section.drives.maneuver.type, section.drives.maneuver.rating, section.size);
      }
    }
    
    // Jump drives
    if (section.drives.jump) {
      // Extract actual tonnage from description if available
      const description = section.drives.jump.description || '';
      const tonsMatch = description.match(/\(([\d.]+) tons\)/);
      if (tonsMatch) {
        totalJumpTons += parseFloat(tonsMatch[1]);
      } else {
        // Fallback to calculated tonnage
        totalJumpTons += getDriveTons(section.drives.jump.type, section.drives.jump.rating, section.size);
      }
    }
    
    // Reaction drives
    if (section.drives.reaction) {
      // Extract actual tonnage from description if available
      const description = section.drives.reaction.description || '';
      const tonsMatch = description.match(/\(([\d.]+) tons\)/);
      if (tonsMatch) {
        totalReactionTons += parseFloat(tonsMatch[1]);
      } else {
        // Fallback to calculated tonnage
        totalReactionTons += getDriveTons(section.drives.reaction.type, section.drives.reaction.rating, section.size);
      }
    }
    
    // Power plants
    if (section.powerPlant) {
      totalPower += section.powerPlant.power || 0;
      totalCost += section.powerPlant.cost || 0;
    }
    
    // Armor (per-section basis as per Traveller rules)
    if (section.armor) {
      totalArmorWeight += section.armor.weight || 0;
      totalArmorCost += section.armor.cost || 0;
    }
  });

  // Calculate combined performance using total tonnage
  const combinedManeuver = calculateManeuverRating(totalManeuverTons, totalTonnage);
  const combinedJump = calculateJumpRating(totalJumpTons, totalTonnage);
  const combinedReaction = calculateReactionRating(totalReactionTons, totalTonnage);

  // Calculate total fuel requirements across all sections
  const totalFuel = sections.reduce((sum, section) => {
    const fuelReqs = calculateSectionFuelRequirements(section);
    return sum + fuelReqs.total;
  }, 0);

  // Debug logging
  console.log('=== COMBINED PERFORMANCE CALCULATION ===');
  console.log('Sections:', sections.map(s => ({
    name: s.name,
    size: s.size,
    drives: s.drives,
    powerPlant: s.powerPlant ? {
      type: s.powerPlant.type,
      rating: s.powerPlant.rating,
      power: s.powerPlant.power,
      cost: s.powerPlant.cost
    } : null
  })));
  console.log('Total Tonnage:', totalTonnage);
  console.log('Drive Tonnages:');
  console.log('  - Maneuver:', totalManeuverTons, 'tons');
  console.log('  - Jump:', totalJumpTons, 'tons');
  console.log('  - Reaction:', totalReactionTons, 'tons');
  console.log('Power Plants:');
  sections.forEach((section, index) => {
    if (section.powerPlant) {
      console.log(`  - ${section.name}: ${section.powerPlant.power || 0} Power (${section.powerPlant.type} ${section.powerPlant.rating})`);
    } else {
      console.log(`  - ${section.name}: No power plant configured`);
    }
  });
  console.log('Calculated Ratings:');
  console.log('  - Maneuver:', combinedManeuver, 'G');
  console.log('  - Jump:', combinedJump, 'parsecs');
  console.log('  - Reaction:', combinedReaction, 'G');
  console.log('Total Power:', totalPower);
  console.log('Total Fuel:', totalFuel);
  console.log('Total Cost:', totalCost);
  console.log('=====================================');

  return {
    maneuver: combinedManeuver,
    jump: combinedJump,
    reaction: combinedReaction,
    totalPower,
    totalFuel,
    totalCost,
    totalArmorWeight,
    totalArmorCost,
  };
};

/**
 * Calculate maneuver drive rating based on tonnage and ship size
 * Following Traveller rules: maneuver drive uses percentOfHull values
 */
export const calculateManeuverRating = (driveTons: number, shipTons: number): number => {
  if (driveTons === 0) return 0;
  
  // Calculate the percentage of hull the drive represents
  const percentOfHull = (driveTons / shipTons) * 100;
  
  // Find the closest rating based on percentOfHull values
  // Maneuver drive percentOfHull values: 0.5%, 1%, 2%, 3%, 4%, 5%, 6%
  const maneuverPercentages = [0.5, 1, 2, 3, 4, 5, 6];
  let closestRating = 0;
  let closestDifference = Infinity;
  
  for (let i = 0; i < maneuverPercentages.length; i++) {
    const difference = Math.abs(maneuverPercentages[i] - percentOfHull);
    if (difference < closestDifference) {
      closestDifference = difference;
      closestRating = i; // Rating 0-6
    }
  }
  
  return closestRating;
};

/**
 * Calculate jump drive rating based on tonnage and ship size
 * Following Traveller rules: jump drive uses percentOfHull values + additional tons
 */
export const calculateJumpRating = (driveTons: number, shipTons: number): number => {
  if (driveTons === 0) return 0;
  
  // Jump drives have additional tons, so we need to account for that
  const percentOfHull = (driveTons / shipTons) * 100;
  
  // Jump drive percentOfHull values from DRIVE_POTENTIAL
  const jumpPercentages = [2.5, 5, 7.5, 10, 12.5, 15, 17.5, 20, 22.5];
  let closestRating = 0;
  let closestDifference = Infinity;
  
  for (let i = 0; i < jumpPercentages.length; i++) {
    // Account for the additional 5 tons in jump drives
    const adjustedPercent = percentOfHull - (5 / shipTons) * 100;
    const difference = Math.abs(jumpPercentages[i] - adjustedPercent);
    if (difference < closestDifference) {
      closestDifference = difference;
      closestRating = i + 1; // Rating 1-9 (jump drives start at rating 1)
    }
  }
  
  return closestRating;
};

/**
 * Calculate reaction drive rating based on tonnage and ship size
 * Following Traveller rules: reaction drive uses percentOfHull values
 */
export const calculateReactionRating = (driveTons: number, shipTons: number): number => {
  if (driveTons === 0) return 0;
  
  // Calculate the percentage of hull the drive represents
  const percentOfHull = (driveTons / shipTons) * 100;
  
  // Find the closest rating based on percentOfHull values
  // Reaction drive percentOfHull values: 0.5%, 1%, 2%, 3%, 4%, 5%, 6%
  const reactionPercentages = [0.5, 1, 2, 3, 4, 5, 6];
  let closestRating = 0;
  let closestDifference = Infinity;
  
  for (let i = 0; i < reactionPercentages.length; i++) {
    const difference = Math.abs(reactionPercentages[i] - percentOfHull);
    if (difference < closestDifference) {
      closestDifference = difference;
      closestRating = i; // Rating 0-6
    }
  }
  
  return closestRating;
};

/**
 * Calculate fuel requirements for a specific section
 * Following Traveller fuel rules
 */
export const calculateSectionFuelRequirements = (
  section: BreakawaySection,
  weeks: number = 4
): { reactionDrive: number; jumpDrive: number; powerPlant: number; total: number } => {
  let reactionDrive = 0;
  let jumpDrive = 0;
  let powerPlant = 0;

  // Reaction drive fuel
  if (section.drives.reaction) {
    const drive = section.drives.reaction;
    const hoursPerWeek = 168;
    const totalHours = weeks * hoursPerWeek;
    
    if (drive.rating === 0) {
      reactionDrive = 0.25 * totalHours; // 0.25 tons per hour for thrust 0
    } else {
      // 2.5% per thrust per hour of ship's total tonnage
      reactionDrive = section.size * 0.025 * drive.rating * totalHours;
    }
  }

  // Jump drive fuel
  if (section.drives.jump) {
    const drive = section.drives.jump;
    jumpDrive = calculateJumpFuelConsumption(drive.rating, section.size);
  }

  // Power plant fuel
  if (section.powerPlant) {
    powerPlant = calculatePowerPlantFuelConsumption(section.powerPlant, weeks);
  }

  const total = reactionDrive + jumpDrive + powerPlant;

  return {
    reactionDrive,
    jumpDrive,
    powerPlant,
    total,
  };
};

/**
 * Calculate breakaway hull cost
 * Following Traveller rules: 2% of combined hull tonnage + MCr2 per ton consumed
 */
export const calculateBreakawayHullCost = (totalTonnage: number): number => {
  const consumedTonnage = Math.ceil(totalTonnage * 0.02); // 2% of total tonnage
  const costPerTon = 2000000; // MCr2 per ton = 2,000,000 Cr per ton
  return consumedTonnage * costPerTon;
};

/**
 * Calculate power requirements for breakaway hull system
 * This calculates the total power requirements for the entire system
 * based on the combined ship size and drives
 */
export const calculateBreakawayPowerRequirements = (
  totalTonnage: number,
  sections: BreakawaySection[],
  additionalPowerRequirements: {
    bridgePower: number;
    sensorsPower: number;
    weaponsPower: number;
    defensiveSystemsPower: number;
    optionalSystemsPower: number;
    stateroomsPower: number;
  }
) => {
  // Get all drives from all sections for combined calculation
  const allDrives = sections.flatMap(section => [
    ...(section.drives.maneuver ? [section.drives.maneuver] : []),
    ...(section.drives.jump ? [section.drives.jump] : []),
    ...(section.drives.reaction ? [section.drives.reaction] : []),
  ]);

  // Calculate basic power requirements for the entire system
  const basicRequirements = calculatePowerRequirements(totalTonnage, allDrives);

  // Add additional power requirements
  const totalAdditionalPower =
    additionalPowerRequirements.bridgePower +
    additionalPowerRequirements.sensorsPower +
    additionalPowerRequirements.weaponsPower +
    additionalPowerRequirements.defensiveSystemsPower +
    additionalPowerRequirements.optionalSystemsPower +
    additionalPowerRequirements.stateroomsPower;

  return {
    ...basicRequirements,
    normalOperations: basicRequirements.normalOperations + totalAdditionalPower,
    jumpOperations: basicRequirements.jumpOperations + totalAdditionalPower,
    maximumLoad: basicRequirements.maximumLoad + totalAdditionalPower,
  };
};

/**
 * Validate breakaway hull configuration
 */
export const validateBreakawayHull = (sections: BreakawaySection[]): { isValid: boolean; errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  sections.forEach((section, index) => {
    // Required systems validation
    if (!section.bridge) {
      errors.push(`${section.name} must have a bridge`);
    }
    
    if (!section.powerPlant) {
      errors.push(`${section.name} must have a power plant`);
    }

    // Optional systems validation
    if (!section.drives.maneuver && !section.drives.jump && !section.drives.reaction) {
      warnings.push(`${section.name} has no drives installed - it cannot move independently`);
    }

    // Fuel validation
    if (section.fuelAllocation.tons < section.fuelAllocation.requirements.total) {
      errors.push(`${section.name} has insufficient fuel (${section.fuelAllocation.tons} tons vs ${section.fuelAllocation.requirements.total} tons required)`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Calculate fuel requirements for breakaway hull system
 * This calculates the total fuel requirements for the entire system
 * based on the combined ship size and drives
 */
export const calculateBreakawayFuelRequirements = (
  totalTonnage: number,
  sections: BreakawaySection[],
  fuelConfig: {
    drives: any[];
    powerPlant: any;
    weeks: number;
    jumpDriveReserveParsecs: number;
  }
) => {
  let reactionDrive = 0;
  let jumpDrive = 0;
  let powerPlant = 0;

  // Calculate reaction drive fuel for all sections combined
  const reactionDrives = fuelConfig.drives.filter((d) => d.type === "reaction");
  reactionDrives.forEach((drive) => {
    const hoursPerWeek = 168; // 7 days × 24 hours
    const totalHours = fuelConfig.weeks * hoursPerWeek;
    
    if (drive.rating === 0) {
      reactionDrive += 0.25 * totalHours; // 0.25 tons per hour for thrust 0
    } else {
      // 2.5% per thrust per hour of ship's total tonnage
      reactionDrive += totalTonnage * 0.025 * drive.rating * totalHours;
    }
  });

  // Calculate jump drive fuel for all sections combined
  const jumpDrives = fuelConfig.drives.filter((d) => d.type === "jump");
  jumpDrives.forEach((drive) => {
    // 10% of ship's total tonnage × jump rating (per jump)
    // According to Traveller rules: 10% of total tonnage × maximum jump score
    jumpDrive += calculateJumpFuelConsumption(drive.rating, totalTonnage);
  });

  // Calculate power plant fuel for the entire system
  if (fuelConfig.powerPlant) {
    powerPlant = calculatePowerPlantFuelConsumption(fuelConfig.powerPlant, fuelConfig.weeks);
  }

  const total = reactionDrive + jumpDrive + powerPlant;

  return {
    reactionDrive: Math.round(reactionDrive),
    jumpDrive: Math.round(jumpDrive),
    powerPlant: Math.round(powerPlant),
    total: Math.round(total),
  };
}; 